// English (canonical). Other locales are typed against `Dict` so they must
// define every key. Interpolation uses {name}-style placeholders.
export const en = {
  // common
  loading: 'Loading…',
  back: '← Back',
  logOut: 'Log out',
  language: 'Language',

  // home
  home_tagline: 'Event photos + challenges. Scan a QR to join, or host your own party.',
  home_joinEvent: 'Join an event',
  home_joinPlaceholder: 'Enter join code (e.g. WS2X9Z)',
  home_join: 'JOIN',
  home_hosting: 'Hosting?',
  home_openConsole: 'OPEN HOST CONSOLE →',
  how_eyebrow: 'How it works',
  how_scan_label: 'Scan',
  how_scan_desc: 'Point your camera at the party QR',
  how_shoot_label: 'Shoot',
  how_shoot_desc: 'Add your best photos to the gallery',
  how_vote_label: 'Vote',
  how_vote_desc: 'Crown the winners of each challenge',

  // app chrome — growth banner + support link, shown site-wide
  growth_pitch: 'Want challenges like this at your own party?',
  growth_cta: 'Create yours →',
  support_contact: 'Support',

  // marketing landing (public root "/")
  mkt_nav_cta: 'Create your event',
  mkt_nav_docs: 'How it works',
  mkt_hero_eyebrow: 'The photo game for your party',
  mkt_hero_title: 'Turn your party into a <em>photo challenge</em>.',
  mkt_hero_sub:
    'Guests scan one QR code, drop their best shots into a live gallery, and vote on fun photo challenges. Winners get revealed at the end of the night — with no app to install.',
  mkt_hero_ctaPrimary: 'Create your event',
  mkt_hero_ctaSecondary: 'See how it works',
  mkt_hero_trust: 'No app for guests · Works on any phone · Set up in minutes',
  mkt_wall_winner: 'Winner',
  mkt_how_eyebrow: 'How it works',
  mkt_how_title: 'Three taps. No downloads.',
  mkt_feat_eyebrow: 'What you get',
  mkt_feat_title: 'Everything your party needs to play.',
  mkt_feat_gallery_title: 'Live photo gallery',
  mkt_feat_gallery_desc: "Every guest's photos land on one shared wall in real time.",
  mkt_feat_challenges_title: 'Photo challenges & voting',
  mkt_feat_challenges_desc: 'Best dressed, best dance move, funniest face — guests vote for the winners.',
  mkt_feat_reveal_title: 'Winner reveal',
  mkt_feat_reveal_desc: 'Crown each challenge with a confetti reveal when the party ends.',
  mkt_feat_qr_title: 'Join with a QR',
  mkt_feat_qr_desc: "Guests scan and they're in. Nothing to download, works on any phone.",
  mkt_feat_branding_title: 'Make it yours',
  mkt_feat_branding_desc: 'Add your own poster and background so it feels like your event.',
  mkt_feat_keep_title: 'Keep every photo',
  mkt_feat_keep_desc: 'Download the whole gallery after the party. Optional 1-year storage.',
  mkt_shots_eyebrow: 'See it live',
  mkt_shots_title: 'Straight from a real party.',
  mkt_shots_gallery: 'The live gallery',
  mkt_shots_voting: 'Vote on challenges',
  mkt_shots_wall: "The winners' wall",
  mkt_price_eyebrow: 'Simple pricing',
  mkt_price_title: 'Pay once per event. No subscription.',
  mkt_price_upto: 'Up to {count} guests',
  mkt_price_incl1: 'Every guest joins free — no app to install',
  mkt_price_incl2: 'Live gallery + photo challenges',
  mkt_price_incl3: 'Winner reveal + downloadable photos',
  mkt_price_note: 'No ads. We never sell your photos. Nothing to pay until you publish your event.',
  mkt_price_cta: 'Create your event',
  mkt_final_title: 'Ready to light up your party?',
  mkt_final_sub: 'Set up your event in a few minutes and share one QR code with your guests.',
  mkt_final_cta: 'Create your event',
  mkt_footer_join: 'Join an event',

  // auth
  auth_login: 'Log in',
  auth_createAccount: 'Create {type} account',
  auth_host: 'host',
  auth_participant: 'participant',
  auth_emailOrUsername: 'Email or username',
  auth_email: 'Email',
  auth_username: 'Username',
  auth_password: 'Password',
  auth_passwordHint: 'At least {min} characters.',
  auth_usernameHint: 'Username: letters, numbers, and . _ - only — no spaces, {min}–{max} characters.',
  auth_emailInvalid: 'Enter a valid email address.',
  auth_logInBtn: 'LOG IN',
  auth_signUp: 'SIGN UP',
  auth_needAccount: 'Need an account? Sign up',
  auth_haveAccount: 'Have an account? Log in',
  auth_genericError: 'Something went wrong',
  auth_forgotPassword: 'Forgot your password?',
  forgot_title: 'Forgot password',
  forgot_subtitle: 'Enter your account email and we’ll send you a link to reset your password.',
  forgot_send: 'SEND RESET LINK',
  forgot_sent: 'If an account exists for that email, a reset link is on its way. Check your inbox (and spam).',
  forgot_backToLogin: 'Back to login',
  reset_title: 'Reset password',
  reset_subtitle: 'Choose a new password for your account.',
  reset_newPassword: 'New password',
  reset_confirmPassword: 'Confirm new password',
  reset_submit: 'SET NEW PASSWORD',
  reset_mismatch: 'Passwords don’t match.',
  reset_done: 'Password updated. Taking you to login…',
  reset_noToken: 'This reset link is invalid or incomplete. Request a new one.',

  // join
  join_subtitle: 'Shoot with your camera → vote for the winners. No app to install.',
  join_pinPlaceholder: 'Join PIN (if required)',
  join_consent: 'I agree my photos may appear in the shared gallery and as challenge candidates.',
  join_joinAsGuest: 'JOIN AS GUEST & ADD PHOTOS',
  join_joinAndAdd: 'JOIN & ADD PHOTOS',
  join_joining: 'JOINING…',
  join_joiningAs: 'Joining as {name}',
  join_notYou: 'not you?',
  join_optionalAccount: '+ Log in / register to get emailed results (optional)',
  join_couldNotJoin: 'Could not join',

  // uploader
  uploader_addPhotos: 'Add photos',
  uploader_left: '{n} LEFT',
  uploader_open: 'OPEN CAMERA / CHOOSE PHOTOS',
  uploader_none: 'NO UPLOADS LEFT',
  uploader_notStarted: "The party hasn't started yet",
  uploader_opensAt: 'Uploads open {time}',
  uploader_capReached: 'Upload limit reached',
  uploader_failed: 'Upload failed',

  // gallery
  gallery_live: 'Live gallery',
  gallery_empty: 'No photos yet — be the first.',
  gallery_locked: 'Photos unlock when the host reveals them.',
  gallery_loadMore: 'LOAD MORE',
  // Social sharing — used by both the live gallery and the results wall.
  share_photo: 'Share photo',
  share_copied: 'Caption copied to clipboard',
  share_failed: 'Could not share — try again',
  // The <a> marker picks the words that become the link back to the site; the
  // URL itself lives in share.ts, not here.
  share_legend: 'Cheer up your events with <a>Fiesta Challenges</a>!',
  // Native-share invite for the event link (host console + guest join page).
  share_invite: 'Join {name} on Fiesta! Snap party photos and vote on the challenges:',
  hostEvent_shareInvite: 'SHARE INVITE',
  join_inviteFriends: 'Invite friends',

  // polls (displayed as "Challenges")
  polls_title: 'Challenges',
  polls_open: 'OPEN',
  polls_voted: 'VOTED ✓',
  polls_closed: 'CLOSED',
  polls_vote: 'VOTE',
  polls_voting: 'VOTING…',
  polls_yourVote: 'your vote',
  polls_photo: 'Photo',
  polls_votesFinal: '{n} votes · final',
  polls_votesLive: '{n} votes · live',
  polls_couldNotVote: 'Could not vote',
  polls_submitPhoto: 'Submit your photo',
  polls_submitHint: 'Enter this challenge with your own photo:',
  polls_submitting: 'Submitting…',
  polls_noSubmissions: 'No submissions yet — be the first!',
  polls_closesIn: 'Voting closes in',
  polls_votingClosed: 'Voting closed — the party is over',

  // results
  results_title: 'RESULTS',
  results_loginPrompt: "Log in to view this event's results.",
  results_loggingIn: 'LOGGING IN…',
  results_noAccess: 'That account does not have access to these results.',
  results_expired: "This event's photos and results have expired and are no longer available.",
  results_noPollsClosed: 'No challenges have closed yet.',
  results_gallery: 'The wall',
  results_openPhoto: 'View photo',
  results_votes: '{n} votes',
  results_winner: 'Winner',
  results_runnersUp: 'Runners-up',
  results_noVotesYet: 'No votes yet',
  results_couldNotLoad: 'Could not load results',
  results_opening: 'Opening results…',
  results_openAtLink: 'Open this event to view results',

  // host console
  host_title: 'Host console',
  host_signedInAs: 'Signed in as {name}',
  host_loginPrompt: 'Log in or create a host account to manage events.',
  host_participantWarning: "You're signed in as a participant ({name}). Host actions need a host account.",
  host_createEvent: '+ CREATE EVENT',
  host_myEvents: 'My events',
  host_noEvents: 'No events yet.',

  // create event
  create_title: 'Create event',
  create_name: 'Event name',
  create_starts: 'Starts',
  create_ends: 'Ends',
  create_capacity: 'Capacity (guests)',
  create_guestsUpTo: 'Up to {count} guests',
  create_revealMode: 'Reveal mode',
  create_revealLive: 'Live (photos appear during the party)',
  create_revealAfter: 'Reveal after',
  create_submit: 'CREATE & PUBLISH',
  create_creating: 'CREATING…',
  create_couldNotCreate: 'Could not create event',

  // host event
  hostEvent_backToEvents: '← My events',
  hostEvent_editName: 'Edit title',
  hostEvent_editDate: 'Edit date',
  hostEvent_save: 'Save',
  hostEvent_cancel: 'Cancel',
  hostEvent_posterPosition: 'Reposition',
  hostEvent_draftNote: 'This event is a draft — publish it to share.',
  hostEvent_publish: 'PUBLISH (test)',
  hostEvent_publishFailed: 'Publish failed (is TEST_MODE on?)',
  hostEvent_scanToJoin: 'Scan to join and start playing the challenges!',
  hostEvent_copyLink: 'COPY JOIN LINK',
  hostEvent_copied: 'COPIED ✓',
  hostEvent_viewResults: 'VIEW RESULTS PAGE →',
  hostEvent_liveResults: 'Live results',
  hostEvent_newPoll: 'New challenge (candidates auto-sourced when opened)',
  hostEvent_pollTitlePlaceholder: 'Challenge title',
  hostEvent_votePhotos: 'Vote on photos',
  hostEvent_votePeople: 'Vote on people',
  hostEvent_voteSubmissions: 'Photo submissions (guests submit)',
  hostEvent_addPoll: 'ADD CHALLENGE',
  hostEvent_open: 'Open',
  hostEvent_close: 'Close',
  hostEvent_candidates: '{n} candidates',
  hostEvent_couldNotLoad: 'Could not load event',
  hostEvent_couldNotCreatePoll: 'Could not create challenge',

  // admin
  admin_title: 'Admin console',
  admin_loginPrompt: 'Log in with a superadmin account.',
  admin_notAdmin: 'This account is not an admin.',
  admin_events: 'Events',
  admin_published: 'Published',
  admin_participants: 'Participants',
  admin_polls: 'Challenges',
  admin_votes: 'Votes',
  admin_photos: 'Photos',
  admin_revenue: 'Revenue',
  admin_colEvent: 'Event',
  admin_colHost: 'Host',
  admin_colStatus: 'Status',
  admin_colFee: 'Paid fee',
  admin_couldNotLoad: 'Could not load dashboard',
  admin_tab_overview: 'Overview',
  admin_tab_events: 'Events',
  admin_tab_polls: 'Challenges',
  admin_tab_users: 'Users',
  admin_colTitle: 'Title',
  admin_colVisibility: 'Tally',
  admin_colCandidates: 'Candidates',
  admin_colRole: 'Role',
  admin_colType: 'Type',
  admin_colEmail: 'Email',
  admin_colHosted: 'Hosted',
  admin_colJoined: 'Joined',
  admin_colVotesCast: 'Votes cast',
  admin_colCreated: 'Created',
  admin_delete: 'Delete',
  admin_deleteConfirm: 'Delete “{name}” and all its data? This cannot be undone.',
  admin_deleting: 'Deleting…',
  admin_detail_participants: 'Participants',
  admin_detail_payments: 'Payments',
  admin_detail_hosted: 'Hosted events',
  admin_detail_memberships: 'Joined events',
  admin_anonymous: 'Anonymous',
  admin_winner: 'winner',
  admin_when: 'When',
  admin_joinCode: 'Join code',
  admin_none: 'None',
  admin_tab_suggestions: 'Suggested challenges',
  admin_tab_emails: 'Emails',
  admin_detail_emails: 'Emails',
  admin_colSource: 'Source',
  admin_colConsent: 'Opt-in',
  admin_colCollected: 'Collected',
  admin_exportCsv: 'Download CSV',
  admin_key: 'Key',
  admin_source: 'Source',
  admin_order: 'Order',
  admin_active: 'Active',
  admin_add: 'Add',
  admin_save: 'Save',
  admin_edit: 'Edit',
  admin_cancel: 'Cancel',
  admin_newSuggestion: 'New suggested challenge',
  admin_yes: 'Yes',
  admin_no: 'No',
  admin_tab_coupons: 'Coupons',
  admin_newCoupon: 'New coupon',
  admin_couponCode: 'Code',
  admin_couponFree: 'Free',
  admin_couponPercent: 'Percent off',
  admin_couponPercentOff: '% off',
  admin_couponMaxRedemptions: 'Max redemptions (blank = ∞)',
  admin_couponExpires: 'Expires (blank = never)',
  admin_couponRedeemed: 'redeemed',

  // poster (host event page)
  hostEvent_poster: 'Party poster',
  hostEvent_addPoster: 'Add poster',
  hostEvent_changePoster: 'Change poster',
  hostEvent_posterUploading: 'Uploading…',
  hostEvent_background: 'Custom background',
  hostEvent_addBackground: 'Add background',
  hostEvent_changeBackground: 'Change background',
  hostEvent_posterHint: 'Portrait works best — about 1080×1350 px (4:5). Keep the main subject centered; edges may be cropped. JPG or PNG, up to 12 MB.',
  hostEvent_backgroundHint: 'Full-screen phone backdrop — use a tall portrait, about 1080×1920 px (9:16). Keep key elements centered; it sits behind a dark overlay. JPG or PNG, up to 12 MB.',
  hostEvent_saveDone: 'Save & done',
  hostEvent_saved: 'Saved ✓',
  hostEvent_photos: 'Photos',
  hostEvent_photosHint: 'Every photo taken at this event, including challenge entries. Delete anything inappropriate — it is removed for everyone, including the projector wall, and cannot be undone.',
  hostEvent_photosEmpty: 'No photos uploaded yet.',
  hostEvent_deletePhoto: 'Delete photo',
  hostEvent_deletePhotoConfirm: 'Permanently delete this photo? This cannot be undone.',
  hostEvent_deleteChallengePhotoConfirm: 'Permanently delete this photo? It is also withdrawn from the challenge "{c}" and any votes for it are discarded. This cannot be undone.',
  hostEvent_deleteAllPhotos: 'Delete all photos',
  hostEvent_deleteAllPhotosConfirm: 'Permanently delete all {n} photos? This cannot be undone.',
  hostEvent_previewInvitation: 'Preview invitation',
  hostEvent_voting: 'Voting',
  hostEvent_votingEnds: 'Voting ends',
  hostEvent_extend1h: 'Extend +1 hour',
  hostEvent_closeVotingNow: 'Close voting now',
  invite_print: 'Print / Save as PDF',
  invite_youreInvited: "You're invited",
  hostEvent_suggestionsHint: 'Pick a suggested challenge or type your own',
  hostEvent_suggestionSelect: '— Pick a suggested challenge —',

  // join email capture
  join_emailOptional: 'Email (optional) — for results & event updates',
  join_marketingOptIn: 'Send me event updates by email',

  // photo source (camera vs library)
  photo_takePhoto: 'Take photo',
  photo_chooseLibrary: 'Photo library',

  // navigation
  nav_back: 'Back',

  // consent popup
  consent_title: 'One quick thing',
  consent_agreeJoin: 'Agree & join',
  consent_cancel: 'Cancel',

  // gallery reliability
  gallery_loadError: "Couldn't load photos — the connection dropped.",
  gallery_errorEnded: 'This party has ended — no new photos can be added.',
  gallery_errorNotMember: 'Join the party again to see the photos.',
  gallery_errorGone: 'This party is no longer available.',
  gallery_retry: 'Try again',

  // participation ticker
  ticker_newPhoto: '📸 New photo just dropped!',
  ticker_newVote: '🔥 Someone just voted!',
  ticker_newPhotoIn: '📸 New photo in {c}!',
  ticker_newVoteIn: '🔥 New vote in {c}!',
  wall_general: 'Gallery',
  wall_winnerOf: 'Winner of',
  wall_timeLeft: 'Time left',
  wall_votingEndsIn: 'Voting ends in:',
  wall_votingClosed: 'Voting is closed',
  wall_partyOverTitle: '🏆 Your winners',
  wall_partyOver: '🏆 That is a wrap — here are your winners!',
  wall_wrapTitle: "🎉 That's a wrap!",
  wall_wrapBody: 'Thanks for celebrating together. The gallery stays open — grab your photos.',

  // projector wall
  wall_join: 'Join the party at',
  wall_empty: 'Waiting for the first photo…',
  wall_photos: 'photos',
  wall_unavailable: "This event's live wall isn't available for public display.",
  hostEvent_openWall: '📺 OPEN LIVE WALL (PROJECTOR)',
  hostEvent_wallHint: 'A full-screen live photo wall to cast onto a TV or projector — no login needed. Works during and after the party.',
  hostEvent_copyWallLink: 'Copy wall link',

  // legal
  footer_privacy: 'Privacy Policy',
  footer_terms: 'Terms of Service',
  footer_deleteAccount: 'Delete account',
  legal_lastUpdated: 'Last updated',
  legal_placeholder: 'This is a starting template — have it reviewed by legal counsel before launch.',

  // payment / publish
  pay_title: 'Publish your event',
  pay_eventFee: 'Event fee',
  pay_storageAddon: '+1-year photo storage',
  pay_total: 'Total',
  pay_promoLabel: 'Promo code',
  pay_promoApply: 'Apply',
  pay_promoApplied: 'Code applied',
  pay_promoFree: 'Free event!',
  pay_promoInvalid: 'That promo code is not valid',
  pay_payAndPublish: 'Pay {amount} & publish',
  pay_publishFree: 'Publish free',
  pay_publishTest: 'Publish (test mode — no charge)',
  pay_notConfigured: 'Payments are not set up yet. Please try again later.',
  pay_processing: 'Processing…',
  pay_backToEdit: '← Back to edit event',

  // published landing
  published_checking_title: 'Confirming your payment…',
  published_checking_subtitle:
    "Hang tight — this usually takes just a few seconds. We'll show your join code as soon as it's confirmed.",
  published_pending_title: 'Almost there — finalizing your event',
  published_pending_subtitle:
    "This is taking a little longer than usual. Your event will go live automatically once payment is confirmed. You can check again, or head to your dashboard — it'll update there too.",
  published_checkAgain: 'Check again',
  published_title: "You're live! 🎉",
  published_subtitle: 'Your event is published. Share the join code with your guests.',
  published_joinCode: 'Join code',
  published_manage: 'Manage event',

  // server errors — mapped from ApiError.code (server stays language-agnostic)
  err_generic: 'Something went wrong. Please try again.',
  err_EVENT_NOT_JOINABLE: 'This event was not found or is not open.',
  err_EVENT_NOT_FOUND: 'Event not found.',
  err_EVENT_ENDED: 'This event has ended.',
  err_EVENT_NOT_STARTED: 'The party has not started yet — uploads open at the start time.',
  err_EVENT_EXPIRED: 'This event has expired.',
  err_NOT_A_MEMBER: 'Join the event first.',
  err_NO_ACCESS: 'You don’t have access to these results.',
  err_ALREADY_VOTED: 'You’ve already voted in this challenge.',
  err_POLL_CLOSED: 'This challenge is closed.',
  err_POLL_NOT_OPEN: 'This challenge isn’t open yet.',
  err_POLL_NOT_FOUND: 'Challenge not found.',
  err_BAD_CANDIDATE: 'Please pick a valid option.',
  err_UPLOAD_CAP_REACHED: 'You’ve reached the upload limit for this event.',
  err_BAD_PIN: 'Wrong join PIN.',
  err_AGE_ACK_REQUIRED: 'You must confirm you’re 18+ to join.',
  err_ACCOUNT_EXISTS: 'That email or username is already in use.',
  err_EMAIL_EXISTS: 'That email is already registered. Try logging in instead.',
  err_USERNAME_EXISTS: 'That username is taken. Please choose another.',
  err_INVALID_CREDENTIALS: 'Invalid email/username or password.',
  err_PASSWORD_BREACHED: 'This password appeared in a data breach. Choose another.',
  err_NOT_A_HOST: 'Only host accounts can do that.',
  err_NOT_HOST: 'Only the host can do that.',
  err_NOT_PAID: 'Pay for the event first.',
  err_ALREADY_PAID: 'This event is already paid.',
  err_ALREADY_EXTENDED: 'This event already has 1-year storage.',
  err_TEST_DISABLED: 'Test publishing is disabled.',
  err_NOT_ADMIN: 'Admin access required.',
  err_RATE_LIMITED: 'Too many attempts. Please wait a moment.',
  err_VALIDATION_ERROR: 'Please check the form and try again.',
  err_UNAUTHORIZED: 'Please log in again.',
  err_COUPON_NOT_FOUND: 'That promo code is not valid',
  err_COUPON_INACTIVE: 'That promo code is no longer active',
  err_COUPON_EXPIRED: 'That promo code has expired',
  err_COUPON_EXHAUSTED: 'That promo code has been fully redeemed',
  err_COUPON_EXISTS: 'A coupon with that code already exists',

  // account deletion (host console)
  account_dangerZone: 'Danger zone',
  account_delete: 'Delete my account',
  account_deleteConfirm:
    'This permanently deletes your account. Events you have already paid for stay live until their normal storage expiry. Enter your password to confirm.',
  account_passwordPlaceholder: 'Your password',
  account_deleteCta: 'Permanently delete',
  account_deleteCancel: 'Cancel',
  account_deleting: 'Deleting…',
  account_deleteError: 'Could not delete your account. Check your password and try again.',

  // marketing unsubscribe / data-erasure page
  unsub_title: 'Marketing emails',
  unsub_intro: 'Manage the marketing emails tied to your email address.',
  unsub_unsubscribe: 'Unsubscribe from marketing emails',
  unsub_unsubscribed: "Done — you won't receive marketing emails from us anymore.",
  unsub_forget: 'Delete my data',
  unsub_forgetHint: 'Permanently remove your email from our marketing list.',
  unsub_forgotten: 'Done — your email has been removed from our marketing list.',
  unsub_missingToken: 'This link is invalid or has expired.',
  unsub_error: 'Something went wrong. Please try again.',
  unsub_working: 'Working…',

  // public account-deletion instructions page (/delete-account)
  del_title: 'Delete your account',
  del_intro:
    'You can permanently delete your Fiesta account at any time. Deletion is immediate and cannot be undone.',
  del_whatTitle: 'What gets removed',
  del_what1: 'Your account and profile — email, username, and login.',
  del_what2: 'Photos you uploaded and any unpublished draft events are permanently erased from storage.',
  del_what3:
    'Events you already paid for stay live until their normal storage expiry, then delete automatically.',
  del_howTitle: 'How to delete your account',
  del_step1: 'Sign in to your account.',
  del_step2: 'Open the host console and find “Delete my account” in the Danger zone.',
  del_step3: 'Enter your password to confirm. Your account is deleted right away.',
  del_cta: 'Sign in to delete',
  del_noLoginTitle: 'Can’t sign in?',
  del_noLogin: 'If you can no longer access your account, email us and we will delete it for you:',
  del_privacyLink: 'See our Privacy Policy for how we handle your data.',
};

export type Dict = typeof en;
